// IndexedDB implementation of the leaf ledger (src/ledger.ts). Same contract
// as MemoryLedgerStore, but backed by a real IndexedDB database so records
// survive a page reload: a `put` resolves only once its write transaction has
// committed, so a resolved promise means the record or marker is durable.
// Write transactions ask for strict durability so `complete` means flushed, not
// merely queued, where the browser honours the hint. Raw IndexedDB API only.

import type { LeafRecord, LedgerStore, RecordStep, ReserveOutcome, TransitionResult, TreeMarker } from './ledger.js';
import { LedgerConflict, UNRESOLVED, applyRecordStep, checkNotReopened, checkRecordWrite, markerKey } from './ledger.js';

const RECORDS = 'records';
const MARKERS = 'markers';
const BY_VAULT_TREE = 'by-vault-tree';

/** Hooks for tests to observe or interrupt IndexedDB requests/transactions at commit boundaries (R23). Product code never passes these. */
export interface IdbHooks {
  /** Called synchronously from the `put` request's `onsuccess`, still inside `update`'s or `transitionRecord`'s transaction, before it is awaited to completion. The signer commits through `transitionRecord` (docs/17 L8), so this is the same commit point it always was. */
  onUpdatePut?: (tx: IDBTransaction, record: LeafRecord) => void;
  /** Called after this store closed its own connection because another connection asked to delete or upgrade the database (a `versionchange` event); the store is unusable from then on. */
  onVersionChange?: () => void;
}

/** Wrap a read request: resolves with its result, rejects on error. */
function reqPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

/** Wrap a transaction: resolves on `complete` (the write is committed), rejects on `error`/`abort`. */
function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

/** Abort `tx` so nothing it did is committed; a transaction that already finished or aborted throws `InvalidStateError`, which changes nothing here. */
function abortQuietly(tx: IDBTransaction): void {
  try {
    tx.abort();
  } catch {
    // already finished or aborted
  }
}

/**
 * Durable ledger store over IndexedDB. Two object stores: `records`
 * (keyPath `key`, indexed by `[vaultId, treeIndex]` for `listRecords`) and
 * `markers` (keyPath `key`). Values are stored as plain objects; every method
 * returns a `structuredClone` copy so callers cannot mutate stored state.
 */
export class IndexedDbLedgerStore implements LedgerStore {
  private constructor(
    private readonly db: IDBDatabase,
    private readonly hooks: IdbHooks,
  ) {}

  /** Create or open database `name` at version 1, creating the object stores on first use. `hooks` are for tests only (R23). */
  static async open(name = 'thevault-ledger', indexedDB: IDBFactory = globalThis.indexedDB, hooks: IdbHooks = {}): Promise<IndexedDbLedgerStore> {
    const db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(RECORDS)) {
          const records = db.createObjectStore(RECORDS, { keyPath: 'key' });
          records.createIndex(BY_VAULT_TREE, ['vaultId', 'treeIndex'], { unique: false });
        }
        if (!db.objectStoreNames.contains(MARKERS)) {
          db.createObjectStore(MARKERS, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error(`failed to open IndexedDB database ${name}`));
    });
    // Version-change cooperation: another tab deleting this database (the
    // app's "Forget this device") is blocked for as long as this connection
    // stays open, so close it at once and tell the owner, who can no longer
    // use this store (R47).
    db.onversionchange = () => {
      db.close();
      hooks.onVersionChange?.();
    };
    return new IndexedDbLedgerStore(db, hooks);
  }

  async getRecord(key: string): Promise<LeafRecord | undefined> {
    const tx = this.db.transaction(RECORDS, 'readonly');
    const r = await reqPromise<LeafRecord | undefined>(tx.objectStore(RECORDS).get(key));
    return r ? structuredClone(r) : undefined;
  }

  /** A raw upsert, except that it refuses to reopen a resolved row (docs/17 L8): get, check and put in one readwrite transaction. */
  async putRecord(record: LeafRecord): Promise<void> {
    const tx = this.db.transaction(RECORDS, 'readwrite', { durability: 'strict' });
    const store = tx.objectStore(RECORDS);
    const existing = await reqPromise<LeafRecord | undefined>(store.get(record.key));
    try {
      checkNotReopened(existing, record);
    } catch (e) {
      abortQuietly(tx);
      throw e;
    }
    store.put(structuredClone(record));
    await txDone(tx);
  }

  /** get-then-put inside one readwrite transaction: IndexedDB serialises readwrite transactions on a store, so the check and the insert are atomic. */
  async reserve(record: LeafRecord): Promise<boolean> {
    const tx = this.db.transaction(RECORDS, 'readwrite', { durability: 'strict' });
    const store = tx.objectStore(RECORDS);
    const existing = await reqPromise<LeafRecord | undefined>(store.get(record.key));
    if (existing) {
      await txDone(tx);
      return false;
    }
    store.put(structuredClone(record));
    await txDone(tx);
    return true;
  }

  /**
   * One readwrite transaction over both object stores (R22): read the tree's
   * marker and its records through `by-vault-tree`, decide eligibility, and
   * `put` only when eligible. IndexedDB serialises readwrite transactions
   * that touch the same stores, so the check and the insert are atomic
   * against another tab reading or writing the same tree concurrently.
   */
  async reserveOrdinary(record: LeafRecord, currentSlot: bigint): Promise<ReserveOutcome> {
    const tx = this.db.transaction([RECORDS, MARKERS], 'readwrite', { durability: 'strict' });
    const recordsStore = tx.objectStore(RECORDS);
    const marker = await reqPromise<TreeMarker | undefined>(tx.objectStore(MARKERS).get(markerKey(record.vaultId, record.treeIndex)));
    const outcome: ReserveOutcome = await (async () => {
      if (!marker) return { ok: false, reason: 'no-marker' } as const;
      if (BigInt(marker.validUntilSlot) < currentSlot) return { ok: false, reason: 'marker-expired' } as const;
      const treeRecords = await reqPromise<LeafRecord[]>(recordsStore.index(BY_VAULT_TREE).getAll([record.vaultId, record.treeIndex]));
      if (treeRecords.some((r) => r.status === 'burned')) return { ok: false, reason: 'retired' } as const;
      if (treeRecords.some((r) => UNRESOLVED.includes(r.status))) return { ok: false, reason: 'unresolved' } as const;
      if (treeRecords.some((r) => r.key === record.key)) return { ok: false, reason: 'exists' } as const;
      return { ok: true } as const;
    })();
    if (outcome.ok) recordsStore.put(structuredClone(record));
    await txDone(tx);
    return outcome;
  }

  /** get-then-put inside one readwrite transaction, as `reserve`, with `transitionRecord`'s checks (docs/17 L8); the `put` request's `onsuccess` fires the R23 test hook before the transaction is awaited to completion. */
  async update(record: LeafRecord): Promise<void> {
    const tx = this.db.transaction(RECORDS, 'readwrite', { durability: 'strict' });
    const store = tx.objectStore(RECORDS);
    const existing = await reqPromise<LeafRecord | undefined>(store.get(record.key));
    try {
      if (!existing) throw new LedgerConflict(`record ${record.key} is absent or carries another digest`);
      checkRecordWrite(record.key, existing, record);
    } catch (e) {
      abortQuietly(tx);
      throw e;
    }
    const putReq = store.put(structuredClone(record));
    putReq.onsuccess = () => this.hooks.onUpdatePut?.(tx, record);
    await txDone(tx);
  }

  /**
   * One readwrite transaction over `records` with strict durability: `get`,
   * the step and its checks, `put`, then await `complete` (docs/17 L8). No
   * other await runs inside it, so it stays active from the read to the
   * write. IndexedDB runs readwrite transactions over one object store one at
   * a time across every connection of the origin, so two tabs' transitions
   * on one row are ordered and the second sees the first's result. A refusal
   * or a throwing step aborts the transaction. The R23 hook fires from this
   * `put` too.
   */
  async transitionRecord(key: string, step: RecordStep): Promise<TransitionResult> {
    const tx = this.db.transaction(RECORDS, 'readwrite', { durability: 'strict' });
    const store = tx.objectStore(RECORDS);
    let stored: LeafRecord | undefined;
    let next: LeafRecord | undefined;
    try {
      stored = await reqPromise<LeafRecord | undefined>(store.get(key));
      next = applyRecordStep(key, stored, step);
    } catch (e) {
      abortQuietly(tx);
      throw e;
    }
    if (next === undefined) {
      await txDone(tx);
      return { record: stored ? structuredClone(stored) : undefined, written: false };
    }
    const written = structuredClone(next);
    const putReq = store.put(written);
    putReq.onsuccess = () => this.hooks.onUpdatePut?.(tx, structuredClone(written));
    await txDone(tx);
    return { record: structuredClone(written), written: true };
  }

  async listRecords(vaultId: string, treeIndex: number): Promise<LeafRecord[]> {
    const tx = this.db.transaction(RECORDS, 'readonly');
    const index = tx.objectStore(RECORDS).index(BY_VAULT_TREE);
    const rows = await reqPromise<LeafRecord[]>(index.getAll([vaultId, treeIndex]));
    return rows.map((r) => structuredClone(r));
  }

  async getMarker(vaultId: string, treeIndex: number): Promise<TreeMarker | undefined> {
    const tx = this.db.transaction(MARKERS, 'readonly');
    const m = await reqPromise<TreeMarker | undefined>(tx.objectStore(MARKERS).get(markerKey(vaultId, treeIndex)));
    return m ? structuredClone(m) : undefined;
  }

  async putMarker(marker: TreeMarker): Promise<void> {
    const tx = this.db.transaction(MARKERS, 'readwrite', { durability: 'strict' });
    tx.objectStore(MARKERS).put(structuredClone(marker));
    await txDone(tx);
  }

  /** Close the underlying connection; the store is unusable afterwards. */
  close(): void {
    this.db.close();
  }
}
